import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogContent,
  FormControl,
  IconButton,
  InputAdornment,
  MenuItem,
  Pagination,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
  useMediaQuery,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import {
  AddRounded as AddRoundedIcon,
  CalendarMonthRounded as CalendarMonthRoundedIcon,
  CheckCircleOutlineRounded as CheckCircleOutlineRoundedIcon,
  CloseRounded as CloseRoundedIcon,
  DownloadRounded as DownloadRoundedIcon,
  EventAvailableRounded as EventAvailableRoundedIcon,
  FilterListRounded as FilterListRoundedIcon,
  NotificationsActiveRounded as NotificationsActiveRoundedIcon,
  RefreshRounded as RefreshRoundedIcon,
  SearchRounded as SearchRoundedIcon,
  ScheduleRounded as ScheduleRoundedIcon,
  DeleteOutlineRounded as DeleteOutlineRoundedIcon,
} from "@mui/icons-material";
import FlexibleStatsRow from "../common/FlexibleStatsRow";

import { useStatus } from "../../popup/NotificationContext";
import {
  useAddReminderMutation,
  useDeleteReminderMutation,
  useGetRemindersQuery,
} from "../../redux/logistics/logisticsApi";

const ROW_OPTIONS = [10, 25, 50, 100];

const fieldSx = {
  "& .MuiOutlinedInput-root": {
    minHeight: 50,
    borderRadius: "16px",
    backgroundColor: "#fff",
    boxShadow: "0 10px 26px rgba(15,23,42,0.04)",
    "& fieldset": {
      borderColor: "rgba(226,232,240,0.95)",
    },
  },
};

const formatDate = (value) => {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return date.toLocaleDateString("en-GB");
};

const getStatusLabel = (item) => {
  const now = new Date();
  const reminderDate = item?.rawDate ? new Date(item.rawDate) : null;
  if (!reminderDate || Number.isNaN(reminderDate.getTime())) return "Active";
  if (reminderDate.toDateString() === now.toDateString()) return "Active";
  if (reminderDate > now) return "Upcoming";
  return "Completed";
};

const getStatusTone = (status) => {
  if (status === "Completed") {
    return { bg: "rgba(168,85,247,0.12)", color: "#9333ea" };
  }
  if (status === "Upcoming") {
    return { bg: "rgba(249,115,22,0.12)", color: "#ea580c" };
  }
  return { bg: "rgba(34,197,94,0.12)", color: "#16a34a" };
};

const buildCsv = (rows) => {
  const headers = ["Name", "Type", "Start", "Period", "Date", "Status"];
  const body = rows.map((row) =>
    [row.name, row.type, row.start, row.period, row.date, row.status]
      .map((value) => `"${String(value ?? "").replace(/"/g, '""')}"`)
      .join(",")
  );
  return [headers.join(","), ...body].join("\n");
};

const ReminderPortalModal = ({ open = true, onClose, asPage = false }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const { showStatus } = useStatus();
  const { data: remindersPayload = [], refetch, isFetching } = useGetRemindersQuery();
  const [addReminder, { isLoading: isSubmitting }] = useAddReminderMutation();
  const [deleteReminder] = useDeleteReminderMutation();

  const [searchQuery, setSearchQuery] = useState("");
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [page, setPage] = useState(1);
  const [formData, setFormData] = useState({
    name: "",
    type: "",
    start: "",
    period: "",
    date: "",
  });

  const reminders = useMemo(
    () =>
      (Array.isArray(remindersPayload) ? remindersPayload : []).map((item) => {
        const status = getStatusLabel(item);
        return {
          ...item,
          rawDate: item?.date,
          name: item?.name || "-",
          type: item?.type || "-",
          start: item?.start ? formatDate(item.start) : "-",
          period: item?.period || "-",
          date: formatDate(item?.date),
          status,
        };
      }),
    [remindersPayload]
  );

  const filteredReminders = useMemo(() => {
    const query = searchQuery.trim().toLowerCase();
    if (!query) return reminders;
    return reminders.filter((item) =>
      [item.name, item.type, item.period, item.start, item.date, item.status]
        .some((value) => String(value || "").toLowerCase().includes(query))
    );
  }, [reminders, searchQuery]);

  const totalPages = Math.max(1, Math.ceil(filteredReminders.length / rowsPerPage));
  const pagedReminders = useMemo(() => {
    const start = (page - 1) * rowsPerPage;
    return filteredReminders.slice(start, start + rowsPerPage);
  }, [filteredReminders, page, rowsPerPage]);

  const summary = useMemo(() => {
    const total = reminders.length;
    const active = reminders.filter((item) => item.status === "Active").length;
    const upcoming = reminders.filter((item) => item.status === "Upcoming").length;
    const completed = reminders.filter((item) => item.status === "Completed").length;
    return { total, active, upcoming, completed };
  }, [reminders]);

  const summaryCards = useMemo(
    () => [
      {
        key: "total",
        label: "Total Reminders",
        value: summary.total,
        detail: "All time",
        tone: "neutral",
        icon: <CalendarMonthRoundedIcon fontSize="small" />,
      },
      {
        key: "active",
        label: "Active Reminders",
        value: summary.active,
        detail: "Currently running",
        tone: "success",
        icon: <CheckCircleOutlineRoundedIcon fontSize="small" />,
      },
      {
        key: "upcoming",
        label: "Upcoming",
        value: summary.upcoming,
        detail: "Next 7 days",
        tone: "warning",
        icon: <ScheduleRoundedIcon fontSize="small" />,
      },
      {
        key: "completed",
        label: "Completed",
        value: summary.completed,
        detail: "This month",
        tone: "violet",
        icon: <EventAvailableRoundedIcon fontSize="small" />,
      },
    ],
    [summary]
  );

  useEffect(() => {
    if (page > totalPages) setPage(1);
  }, [page, totalPages]);

  const resetForm = () => {
    setFormData({
      name: "",
      type: "",
      start: "",
      period: "",
      date: "",
    });
  };

  const handleDownloadCsv = () => {
    const csv = buildCsv(filteredReminders);
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement("a");
    anchor.href = url;
    anchor.download = "reminders.csv";
    document.body.appendChild(anchor);
    anchor.click();
    document.body.removeChild(anchor);
    URL.revokeObjectURL(url);
    showStatus("success", "Reminders CSV downloaded.");
  };

  const handleSubmit = async () => {
    if (!formData.name.trim() || !formData.type.trim() || !formData.date) {
      showStatus("warning", "Name, type, aur date required hain.");
      return;
    }
    try {
      await addReminder({
        ...formData,
        start: formData.start || null,
      }).unwrap();
      await refetch();
      resetForm();
      setPage(1);
      showStatus("success", "Reminder added successfully.");
    } catch (error) {
      showStatus("error", error?.data?.error || "Failed to add reminder.");
    }
  };

  const handleDelete = async (id) => {
    if (!id) return;
    if (!window.confirm("Reminder delete karna hai?")) return;
    try {
      await deleteReminder(id).unwrap();
      showStatus("success", "Reminder deleted.");
    } catch (error) {
      showStatus("error", error?.data?.error || "Failed to delete reminder.");
    }
  };

  const focusForm = () => {
    resetForm();
    const formNode = document.getElementById("reminder-form-card");
    if (formNode) {
      formNode.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  };

  const listCard = (
    <Paper
      elevation={0}
      sx={{
        borderRadius: "24px",
        border: "1px solid rgba(226,232,240,0.92)",
        boxShadow: "0 18px 42px rgba(15,23,42,0.05)",
        overflow: "hidden",
      }}
    >
      <Box sx={{ p: { xs: 1.5, md: 2.2 }, borderBottom: "1px solid rgba(226,232,240,0.9)" }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center" gap={1.5}>
          <Typography sx={{ fontSize: "1.7rem", fontWeight: 800, color: "#0f172a" }}>Reminders List</Typography>
          {!asPage ? (
            <IconButton onClick={onClose} sx={{ border: "1px solid rgba(226,232,240,0.95)" }}>
              <CloseRoundedIcon />
            </IconButton>
          ) : null}
        </Stack>
      </Box>

      <Box sx={{ p: { xs: 1.5, md: 2.2 }, borderBottom: "1px solid rgba(226,232,240,0.88)" }}>
        <Stack direction={{ xs: "column", xl: "row" }} spacing={1.5} justifyContent="space-between">
          <Stack direction={{ xs: "column", md: "row" }} spacing={1.25} sx={{ flex: 1 }}>
            <TextField
              value={searchQuery}
              onChange={(event) => {
                setSearchQuery(event.target.value);
                setPage(1);
              }}
              placeholder="Search reminders..."
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchRoundedIcon sx={{ color: "#64748b" }} />
                  </InputAdornment>
                ),
              }}
              sx={{ ...fieldSx, minWidth: { xs: "100%", md: 320 } }}
            />
            <Button
              startIcon={<FilterListRoundedIcon />}
              sx={{
                minWidth: 110,
                height: 50,
                borderRadius: "16px",
                border: "1px solid rgba(226,232,240,0.95)",
                bgcolor: "#fff",
                color: "#334155",
                textTransform: "none",
                fontWeight: 700,
                boxShadow: "0 10px 26px rgba(15,23,42,0.03)",
              }}
            >
              Filters
            </Button>
            <FormControl sx={{ minWidth: 92 }}>
              <Select
                value={rowsPerPage}
                onChange={(event) => {
                  setRowsPerPage(Number(event.target.value));
                  setPage(1);
                }}
                sx={{
                  height: 50,
                  borderRadius: "16px",
                  bgcolor: "#fff",
                  boxShadow: "0 10px 26px rgba(15,23,42,0.03)",
                  "& .MuiOutlinedInput-notchedOutline": {
                    borderColor: "rgba(226,232,240,0.95)",
                  },
                }}
              >
                {ROW_OPTIONS.map((size) => (
                  <MenuItem key={size} value={size}>{size}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Stack>

          <Stack direction="row" spacing={1.1} justifyContent="flex-end">
            <Tooltip title="Refresh">
              <IconButton
                onClick={() => refetch()}
                sx={{
                  width: 50,
                  height: 50,
                  borderRadius: "16px",
                  border: "1px solid rgba(226,232,240,0.95)",
                  bgcolor: "#fff",
                  boxShadow: "0 10px 26px rgba(15,23,42,0.03)",
                }}
              >
                <RefreshRoundedIcon />
              </IconButton>
            </Tooltip>
            <Tooltip title="Download CSV">
              <IconButton
                onClick={handleDownloadCsv}
                sx={{
                  width: 50,
                  height: 50,
                  borderRadius: "16px",
                  border: "1px solid rgba(226,232,240,0.95)",
                  bgcolor: "#fff",
                  boxShadow: "0 10px 26px rgba(15,23,42,0.03)",
                }}
              >
                <DownloadRoundedIcon />
              </IconButton>
            </Tooltip>
          </Stack>
        </Stack>
      </Box>

      {!isMobile ? (
        <TableContainer sx={{ px: 2, pt: 2, minHeight: 430 }}>
          <Table>
            <TableHead>
              <TableRow>
                {["Name", "Type", "Start", "Period", "Date", "Status", "Action"].map((head) => (
                  <TableCell
                    key={head}
                    sx={{
                      borderBottom: "1px solid rgba(226,232,240,0.9)",
                      color: "#64748b",
                      fontSize: "0.85rem",
                      fontWeight: 700,
                      py: 2.2,
                    }}
                  >
                    {head}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {pagedReminders.length ? (
                pagedReminders.map((item) => {
                  const tone = getStatusTone(item.status);
                  return (
                    <TableRow key={item._id} hover sx={{ "& td": { py: 2.2, borderBottom: "1px solid rgba(241,245,249,1)" } }}>
                      <TableCell sx={{ fontWeight: 800, color: "#0f172a" }}>{item.name}</TableCell>
                      <TableCell sx={{ color: "#334155", fontWeight: 700 }}>{item.type}</TableCell>
                      <TableCell sx={{ color: "#334155", fontWeight: 700 }}>{item.start}</TableCell>
                      <TableCell sx={{ color: "#334155", fontWeight: 700 }}>{item.period}</TableCell>
                      <TableCell sx={{ color: "#334155", fontWeight: 700 }}>{item.date}</TableCell>
                      <TableCell>
                        <Chip
                          label={item.status}
                          size="small"
                          sx={{
                            borderRadius: "999px",
                            bgcolor: tone.bg,
                            color: tone.color,
                            fontWeight: 700,
                          }}
                        />
                      </TableCell>
                      <TableCell>
                        <Tooltip title="Delete Reminder">
                          <IconButton
                            size="small"
                            onClick={() => handleDelete(item._id)}
                            sx={{
                              width: 40,
                              height: 40,
                              borderRadius: "12px",
                              border: "1px solid rgba(239,68,68,0.25)",
                              bgcolor: "rgba(239,68,68,0.08)",
                              color: "#dc2626",
                            }}
                          >
                            <DeleteOutlineRoundedIcon sx={{ fontSize: 18 }} />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  );
                })
              ) : (
                <TableRow>
                  <TableCell colSpan={7} sx={{ py: 8 }}>
                    <Stack spacing={1.25} alignItems="center" justifyContent="center">
                      <Box sx={{ width: 96, height: 96, borderRadius: "50%", bgcolor: "rgba(59,130,246,0.08)", color: "#2563eb", display: "grid", placeItems: "center" }}>
                        <NotificationsActiveRoundedIcon sx={{ fontSize: 40 }} />
                      </Box>
                      <Typography sx={{ fontWeight: 800, fontSize: "1.2rem", color: "#0f172a" }}>
                        No reminders found
                      </Typography>
                      <Typography sx={{ color: "#64748b" }}>
                        Create your first reminder to get started.
                      </Typography>
                      <Button
                        variant="contained"
                        startIcon={<AddRoundedIcon />}
                        onClick={focusForm}
                        sx={{ borderRadius: "14px", mt: 1, textTransform: "none", fontWeight: 700, boxShadow: "0 14px 28px rgba(37,99,235,0.20)" }}
                      >
                        Add Reminder
                      </Button>
                    </Stack>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      ) : (
        <Box sx={{ p: 1.5, display: "grid", gap: 1.25 }}>
          {pagedReminders.length ? (
            pagedReminders.map((item) => {
              const tone = getStatusTone(item.status);
              return (
                <Paper
                  key={item._id}
                  elevation={0}
                  sx={{
                    p: 1.5,
                    borderRadius: "18px",
                    border: "1px solid rgba(226,232,240,0.95)",
                    boxShadow: "0 12px 26px rgba(15,23,42,0.04)",
                  }}
                >
                  <Stack spacing={1}>
                    <Stack direction="row" justifyContent="space-between" alignItems="center" gap={1}>
                      <Typography sx={{ fontWeight: 800, color: "#0f172a" }}>{item.name}</Typography>
                      <Chip label={item.status} size="small" sx={{ borderRadius: "999px", bgcolor: tone.bg, color: tone.color, fontWeight: 700 }} />
                    </Stack>
                    <Typography sx={{ color: "#64748b", fontSize: "0.86rem" }}>Type: {item.type}</Typography>
                    <Typography sx={{ color: "#64748b", fontSize: "0.86rem" }}>Start: {item.start}</Typography>
                    <Typography sx={{ color: "#64748b", fontSize: "0.86rem" }}>Period: {item.period}</Typography>
                    <Typography sx={{ color: "#64748b", fontSize: "0.86rem" }}>Date: {item.date}</Typography>
                    <Box>
                      <Tooltip title="Delete Reminder">
                        <IconButton
                          size="small"
                          onClick={() => handleDelete(item._id)}
                          sx={{
                            width: 40,
                            height: 40,
                            borderRadius: "12px",
                            border: "1px solid rgba(239,68,68,0.25)",
                            bgcolor: "rgba(239,68,68,0.08)",
                            color: "#dc2626",
                          }}
                        >
                          <DeleteOutlineRoundedIcon sx={{ fontSize: 18 }} />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </Stack>
                </Paper>
              );
            })
          ) : (
            <Paper sx={{ py: 7, textAlign: "center", borderRadius: 3 }}>
              <Stack spacing={1.25} alignItems="center">
                <Box sx={{ width: 88, height: 88, borderRadius: "50%", bgcolor: "rgba(59,130,246,0.08)", color: "#2563eb", display: "grid", placeItems: "center" }}>
                  <NotificationsActiveRoundedIcon sx={{ fontSize: 36 }} />
                </Box>
                <Typography sx={{ fontWeight: 800, fontSize: "1.1rem", color: "#0f172a" }}>No reminders found</Typography>
                <Typography sx={{ color: "#64748b", px: 2 }}>Create your first reminder to get started.</Typography>
              </Stack>
            </Paper>
          )}
        </Box>
      )}

      <Box
        sx={{
          px: { xs: 1.5, md: 2.5 },
          py: 2,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          gap: 1.5,
          flexWrap: "wrap",
          borderTop: "1px solid rgba(226,232,240,0.88)",
        }}
      >
        <Typography sx={{ color: "#475569", fontSize: "0.92rem" }}>
          Showing {filteredReminders.length ? (page - 1) * rowsPerPage + 1 : 0} to {Math.min(page * rowsPerPage, filteredReminders.length)} of {filteredReminders.length} entries
        </Typography>
        <Pagination
          page={page}
          count={totalPages}
          onChange={(_, value) => setPage(value)}
          shape="rounded"
          sx={{
            "& .MuiPagination-ul": { gap: 0.6 },
            "& .MuiPaginationItem-root": {
              borderRadius: "12px",
              minWidth: 38,
              height: 38,
              border: "1px solid rgba(226,232,240,0.95)",
              color: "#334155",
              fontWeight: 700,
              bgcolor: "#fff",
            },
            "& .Mui-selected": {
              bgcolor: "#1d63c8 !important",
              color: "#fff",
              boxShadow: "0 14px 28px rgba(29,99,200,0.22)",
              borderColor: "#1d63c8",
            },
          }}
        />
      </Box>
    </Paper>
  );

  const formCard = (
    <Paper
      id="reminder-form-card"
      elevation={0}
      sx={{
        p: { xs: 1.5, md: 2.2 },
        borderRadius: "24px",
        border: "1px solid rgba(226,232,240,0.92)",
        boxShadow: "0 18px 42px rgba(15,23,42,0.05)",
        position: { lg: "sticky" },
        top: { lg: 24 },
      }}
    >
      <Typography sx={{ fontSize: "1.7rem", fontWeight: 800, color: "#0f172a", mb: 2.2 }}>Add Reminder</Typography>

      <Stack spacing={2}>
        <Box>
          <Typography sx={{ mb: 1, fontSize: 14, fontWeight: 700, color: "#334155" }}>Name</Typography>
          <TextField
            fullWidth
            value={formData.name}
            onChange={(event) => setFormData((prev) => ({ ...prev, name: event.target.value }))}
            placeholder="Enter reminder name"
            sx={fieldSx}
          />
        </Box>

        <Box>
          <Typography sx={{ mb: 1, fontSize: 14, fontWeight: 700, color: "#334155" }}>Type</Typography>
          <TextField
            fullWidth
            value={formData.type}
            onChange={(event) => setFormData((prev) => ({ ...prev, type: event.target.value }))}
            placeholder="Select type"
            sx={fieldSx}
          />
        </Box>

        <Box>
          <Typography sx={{ mb: 1, fontSize: 14, fontWeight: 700, color: "#334155" }}>Start</Typography>
          <TextField
            fullWidth
            value={formData.start}
            onChange={(event) => setFormData((prev) => ({ ...prev, start: event.target.value }))}
            placeholder="--:-- --"
            sx={fieldSx}
          />
        </Box>

        <Box>
          <Typography sx={{ mb: 1, fontSize: 14, fontWeight: 700, color: "#334155" }}>Date</Typography>
          <TextField
            fullWidth
            type="date"
            value={formData.date}
            onChange={(event) => setFormData((prev) => ({ ...prev, date: event.target.value }))}
            sx={fieldSx}
            InputLabelProps={{ shrink: true }}
          />
        </Box>

        <Box>
          <Typography sx={{ mb: 1, fontSize: 14, fontWeight: 700, color: "#334155" }}>Period</Typography>
          <TextField
            fullWidth
            value={formData.period}
            onChange={(event) => setFormData((prev) => ({ ...prev, period: event.target.value }))}
            placeholder="Select period"
            sx={fieldSx}
          />
        </Box>

        <Stack direction="row" spacing={1.25} sx={{ pt: 1 }}>
          <Button
            variant="outlined"
            onClick={resetForm}
            sx={{
              flex: 1,
              height: 50,
              borderRadius: "14px",
              textTransform: "none",
              fontWeight: 700,
              borderColor: "rgba(203,213,225,0.95)",
              color: "#334155",
            }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleSubmit}
            disabled={isSubmitting}
            sx={{
              flex: 1,
              height: 50,
              borderRadius: "14px",
              textTransform: "none",
              fontWeight: 700,
              bgcolor: "#1d63c8",
              boxShadow: "0 16px 30px rgba(29,99,200,0.24)",
              "&:hover": { bgcolor: "#114fae" },
            }}
          >
            {isSubmitting ? "Submitting..." : "Submit"}
          </Button>
        </Stack>
      </Stack>
    </Paper>
  );

  const content = (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2.2 }}>
      {isFetching ? (
        <Alert severity="info" sx={{ borderRadius: 2.5 }}>
          Refreshing reminders...
        </Alert>
      ) : null}

      <FlexibleStatsRow items={summaryCards} />

      <Stack direction="row" justifyContent="flex-end">
        <Button
          variant="contained"
          startIcon={<AddRoundedIcon />}
          onClick={focusForm}
          sx={{
            height: 52,
            minWidth: 156,
            borderRadius: "16px",
            textTransform: "none",
            fontWeight: 700,
            bgcolor: "#1d63c8",
            boxShadow: "0 16px 30px rgba(29,99,200,0.24)",
            "&:hover": { bgcolor: "#114fae" },
          }}
        >
          Add Reminder
        </Button>
      </Stack>

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", lg: "minmax(0, 1.9fr) minmax(320px, 0.75fr)" },
          gap: 2,
          alignItems: "start",
        }}
      >
        {listCard}
        {formCard}
      </Box>
    </Box>
  );

  if (asPage) {
    return content;
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="xl"
      PaperProps={{
        sx: {
          borderRadius: 3,
          overflow: "hidden",
          minHeight: "80vh",
          background: "linear-gradient(180deg, #f7f9fd 0%, #f3f6fb 100%)",
        },
      }}
    >
      <DialogContent sx={{ p: 2.5 }}>{content}</DialogContent>
    </Dialog>
  );
};

export default ReminderPortalModal;
